import {
  LUNA_ASSISTANT_API,
  LUNA_RATE_API,
  LUNA_SEARCH_API,
} from 'config/constants';
import type { LunaSearchRequest, LunaSearchStreamChunk } from 'types/search';
import { getCookie } from './UtilityService';

export type LunaAssistantTask = 'suggest' | 'summarize' | 'expand';

export interface LunaAssistantRequest {
  uuid: string;
  slug: string; // ad141/9.0/ch01
  task: LunaAssistantTask;
  statement: string;
  fullPage?: boolean;
}

export interface LunaAssistantRelatedItem {
  id?: string; // e.g. ad141/9.0/ch10
  offering_title?: string;
  page_title?: string;
  corpuses?: string[];
  objective?: string;
  _score?: number;
  [key: string]: unknown;
}

export interface LunaAssistantStatus {
  isTooShort?: boolean;
  isTooLong?: boolean;
  /**
   * Indicates Luna is temporarily unavailable (e.g. due to high system usage).
   */
  isUnavailable?: boolean;
  canSummarize?: boolean;
  canExpand?: boolean;
  noResultsFound?: boolean;
  didNotValidate?: boolean;
  [key: string]: unknown;
}

export interface LunaAssistantChunk {
  ref?: string;
  output?: string; // HTML
  related?: LunaAssistantRelatedItem[];
  status?: LunaAssistantStatus;
  [key: string]: unknown;
}

export class LunaRequestError extends Error {
  status?: number;

  statusText?: string;

  contentType?: string;

  responseSnippet?: string;

  constructor(
    message: string,
    init?: {
      status?: number;
      statusText?: string;
      contentType?: string;
      responseSnippet?: string;
    },
  ) {
    super(message);
    this.name = 'LunaRequestError';
    this.status = init?.status;
    this.statusText = init?.statusText;
    this.contentType = init?.contentType;
    this.responseSnippet = init?.responseSnippet;
  }
}

const MAX_ERROR_SNIPPET = 2000;

/**
 * Backend can stream multiple JSON objects (NDJSON-like).
 * We parse the latest valid JSON object and return it.
 */
function parseLatestJsonObject(text: string): LunaAssistantChunk {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  // Try from the end backwards until JSON.parse works.
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    try {
      return JSON.parse(lines[i]) as LunaAssistantChunk;
    } catch (e) {
      // keep going
    }
  }

  // Fallback: maybe it's a single JSON object (not line-based)
  try {
    return JSON.parse(text) as LunaAssistantChunk;
  } catch (e) {
    return {};
  }
}

export type LunaAssistantStreamOptions = {
  onChunk?: (chunk: LunaAssistantChunk) => void;
  signal?: AbortSignal;
};

export enum LunaRateValue {
  ThumbsUp = 1,
  NoRating = 0,
  ThumbsDown = -1,
}

export interface LunaRateRequest {
  /**
   * The id of the inference chunk that was recommended by Luna in the previous call to streamLunaRecommendations.
   */
  inference_ref: string;
  /**
   * The rating to submit for the inference chunk.
   */
  rating: LunaRateValue;
  /**
   * A comment to submit for the inference chunk. Optional.
   */
  comment?: string;
}

export type LunaRateResponse = Record<string, unknown>;

export type LunaRateOptions = {
  signal?: AbortSignal;
};

/**
 * Stream recommendations as JSON Lines (content-type: text/jsonl).
 * Each line is a JSON object snapshot; we call onChunk for each parsed line.
 */
export const streamLunaRecommendations = async (
  payload: LunaAssistantRequest,
  options: LunaAssistantStreamOptions = {},
): Promise<LunaAssistantChunk> => {
  const { onChunk, signal } = options;
  const token = getCookie('csrftoken');

  const response = await fetch(LUNA_ASSISTANT_API, {
    method: 'POST',
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      ...(token ? { 'x-CSRFToken': token } : {}),
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
    signal,
  });

  const contentType = response.headers.get('content-type') ?? '';

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new LunaRequestError(`Request failed (${response.status})`, {
      status: response.status,
      statusText: response.statusText,
      contentType,
      responseSnippet: errText.slice(0, MAX_ERROR_SNIPPET),
    });
  }

  if (contentType.includes('text/html')) {
    const errText = await response.text().catch(() => '');
    throw new LunaRequestError(
      `Unexpected HTML response (${response.status})`,
      {
        status: response.status,
        statusText: response.statusText,
        contentType,
        responseSnippet: errText.slice(0, MAX_ERROR_SNIPPET),
      },
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    // Fallback (non-streaming)
    const fullText = await response.text();
    const latest = parseLatestJsonObject(fullText);
    onChunk?.(latest);
    return latest;
  }

  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let latest: LunaAssistantChunk = {};

  const flushLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      const chunk = JSON.parse(trimmed) as LunaAssistantChunk;
      latest = chunk;
      onChunk?.(chunk);
    } catch (e) {
      // ignore partial/bad lines
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Streaming reads must be sequential; disabling this rule is appropriate here.
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;

    // Append to buffer because a JSONL line can arrive split across chunks.
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach(flushLine);
  }

  buffer += decoder.decode();
  if (buffer) flushLine(buffer);
  return latest;
};

/**
 * Submit a rating to the Luna rate API (thumbs up/down).
 * Non-streaming: POST returns full JSON response.
 */
export const submitLunaRate = async (
  payload: LunaRateRequest,
  options: LunaRateOptions = {},
): Promise<LunaRateResponse> => {
  const { signal } = options;
  const token = getCookie('csrftoken');

  const response = await fetch(LUNA_RATE_API, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'x-CSRFToken': token } : {}),
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
    signal,
  });

  const contentType = response.headers.get('content-type') ?? '';

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new LunaRequestError(`Request failed (${response.status})`, {
      status: response.status,
      statusText: response.statusText,
      contentType,
      responseSnippet: errText.slice(0, MAX_ERROR_SNIPPET),
    });
  }

  if (contentType.includes('text/html')) {
    const errText = await response.text().catch(() => '');
    throw new LunaRequestError(
      `Unexpected HTML response (${response.status})`,
      {
        status: response.status,
        statusText: response.statusText,
        contentType,
        responseSnippet: errText.slice(0, MAX_ERROR_SNIPPET),
      },
    );
  }

  const text = await response.text();
  try {
    return (text ? JSON.parse(text) : {}) as LunaRateResponse;
  } catch (e) {
    throw new LunaRequestError('Invalid JSON response from rate API', {
      status: response.status,
      statusText: response.statusText,
      contentType,
      responseSnippet: text.slice(0, MAX_ERROR_SNIPPET),
    });
  }
};

export type { LunaSearchRequest, LunaSearchStreamChunk };

const parseLunaSearchLine = (line: string): LunaSearchStreamChunk | null => {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as LunaSearchStreamChunk;
  } catch (e) {
    return null;
  }
};

async function* readNdjson(
  response: Response,
): AsyncGenerator<LunaSearchStreamChunk> {
  const reader = response.body?.getReader();
  if (!reader) {
    const fullText = await response.text();
    // eslint-disable-next-line no-restricted-syntax
    for (const line of fullText.split('\n')) {
      const chunk = parseLunaSearchLine(line);
      if (chunk) {
        yield chunk;
      }
    }
    return;
  }

  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Streaming reads must be sequential.
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    // eslint-disable-next-line no-restricted-syntax
    for (const line of lines) {
      const chunk = parseLunaSearchLine(line);
      if (chunk) {
        yield chunk;
      }
    }
  }

  buffer += decoder.decode();
  if (buffer) {
    const chunk = parseLunaSearchLine(buffer);
    if (chunk) {
      yield chunk;
    }
  }
}

/**
 * POST the search term to the Luna search proxy.
 * Yields each NDJSON object: first is text-search hits, later ones stream
 * the AI overview HTML in `summary`.
 */
export async function* searchLuna(
  searchTerm: string,
  { signal }: { signal?: AbortSignal } = {},
): AsyncGenerator<LunaSearchStreamChunk> {
  const token = getCookie('csrftoken');

  const response = await fetch(LUNA_SEARCH_API, {
    method: 'POST',
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      ...(token ? { 'x-CSRFToken': token } : {}),
    },
    body: JSON.stringify({ queries: [searchTerm] } as LunaSearchRequest),
    credentials: 'same-origin',
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new LunaRequestError(`Request failed (${response.status})`, {
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get('content-type') ?? '',
      responseSnippet: errText.slice(0, MAX_ERROR_SNIPPET),
    });
  }

  yield* readNdjson(response);
}
